// src/pages/msp-inventory/components/reports/LineItemsDrawer.jsx
// Reusable "what makes up this number" drawer. Give it the same range
// params the report used plus an optional filter (month / date / supplier)
// and it lists every order line behind that total, flags missing costs,
// lets you fill in a missing buy cost inline, and can regroup the lines by
// listing (best sellers). Clicking an item opens its SKU report in a popup.
// Every number has a hover breakdown.
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X, AlertTriangle, List, Layers, CheckCircle2, Search, Package, Copy, Check } from "lucide-react";
import { getReportLineItems, setItemCosts } from "../../services/catalogService";
import { SortableTH, Pagination } from "./TableControls";
import { useSortAndPaginate } from "../../hooks/useSortAndPaginate";
import ExportCsvButton from "./ExportCsvButton";
import { downloadCsv } from "../../utils/exportCsv";
import SkuDetailPage from "../../pages/reports/SkuDetailPage";

const money = (v) => `$${Number(v || 0).toFixed(2)}`;
const sum = (rows, key) => rows.reduce((acc, r) => acc + Number(r[key] || 0), 0);
const needsUnitCost = (r) => (r.missing_fields || []).includes("unit_cost");
const needsFees = (r) => (r.missing_fields || []).includes("fees");
// "2026-09-14" -> "09/14/26"
const shortDate = (d) => (d && d.length >= 10 ? `${d.slice(5, 7)}/${d.slice(8, 10)}/${d.slice(2, 4)}` : d || "");
const pct = (num, den) => (den ? ((num / den) * 100).toFixed(1) : "0.0");
const NUM = "border-b border-dotted border-gray-300"; // marks a hoverable number

const MISSING_LABELS = {
    unit_cost: "No cost price",
    fees: "No fee data",
    unlinked: "Not linked to an item",
};
const MISSING_HELP = {
    unit_cost: "No buy cost for this item, so COGS is understated and profit overstated. Type the cost into the box on this row and press Save.",
    fees: "No fee data for this item. Go to your Catalog and run Backfill Fees.",
    unlinked: "This order isn't linked to an item in your catalog (for example, the SKU changed or was removed), so no cost or fees can be applied.",
};

const FEE_LABELS = [
    ["referral_fee", "Referral"],
    ["wfs_fee", "WFS fee"],
    ["fulfillment_cost", "Fulfillment"],
    ["inbound_shipping", "Inbound shipping"],
    ["sales_tax", "Sales tax (on cost)"],
];
const sumFeeBreakdown = (rows) => {
    const out = {};
    FEE_LABELS.forEach(([k]) => { out[k] = rows.reduce((a, r) => a + Number(r.fee_breakdown?.[k] || 0), 0); });
    return out;
};

// ── Tooltip (black, fixed-positioned so scroll containers can't clip it) ──
const Tip = ({ content, children, className = "" }) => {
    const [pos, setPos] = useState(null);
    if (!content) return <>{children}</>;
    const show = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setPos({ left: r.left, right: r.right, top: r.top, bottom: r.bottom });
    };

    // Near the right edge → tooltip grows LEFT from the item's right side.
    // Near the left edge → grows RIGHT from the item's left side.
    // Otherwise → centered. clientWidth excludes the scrollbar.
    let style = null;
    if (pos) {
        const vw = document.documentElement.clientWidth;
        const above = pos.top > 640;
        const center = (pos.left + pos.right) / 2;
        let tx = "-50%";
        style = { top: above ? pos.top - 6 : pos.bottom + 6 };
        if (center > vw * 0.5) {
            style.right = Math.max(8, vw - pos.right);
            tx = "0";
        } else if (center < 150) {
            style.left = Math.max(8, pos.left);
            tx = "0";
        } else {
            style.left = center;
        }
        style.transform = `translate(${tx}, ${above ? "-100%" : "0"})`;
    }

    return (
        <span className={`cursor-help ${className}`} onMouseEnter={show} onMouseLeave={() => setPos(null)}>
            {children}
            {pos && (
                <span className="fixed z-[10002] block pointer-events-none bg-gray-900 text-white text-[11px] leading-snug font-normal normal-case text-left rounded-lg shadow-xl px-2.5 py-2 w-max max-w-[260px]"
                    style={style}>
                    {content}
                </span>
            )}
        </span>
    );
};

const BreakdownTip = ({ title, rows = [], total, note }) => (
    <span className="block min-w-[190px]">
        {title && <span className="block text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">{title}</span>}
        {rows.map(([label, value]) => (
            <span key={label} className="flex justify-between gap-4">
                <span className="text-gray-300">{label}</span>
                <span className="font-semibold">{value}</span>
            </span>
        ))}
        {total && (
            <span className={`flex justify-between gap-4 font-bold ${rows.length ? "border-t border-gray-700 mt-1 pt-1" : ""}`}>
                <span>{total[0]}</span><span>{total[1]}</span>
            </span>
        )}
        {note && <span className="block text-gray-400 mt-1">{note}</span>}
    </span>
);

const COGS_SOURCE_NOTE = {
    fifo: "Cost Price taken from your replen lots (FIFO).",
    fifo_partial: "Partly from replen lots (FIFO). Units beyond your lots use the item's current Cost Price.",
    catalog: "Cost Price from the item's catalog cost.",
};

const revenueTip = (revenue, qty, shipping) => (
    <BreakdownTip
        title="Revenue"
        rows={[
            ...(qty ? [["Per unit", money(revenue / qty)], ["Units", qty]] : []),
            ...(shipping > 0 ? [["Incl. shipping charged", money(shipping)]] : []),
        ]}
        total={["Total charged", money(revenue)]}
    />
);

const cogsTip = ({ costPrice, extra, cogs, source, missing }) => (
    <BreakdownTip
        title="Cost of goods"
        rows={[["Cost Price", money(costPrice)], ["Other costs (prep, etc.)", money(extra)]]}
        total={["Total COGS", money(cogs)]}
        note={missing ? "Missing buy cost, so this COGS is understated." : COGS_SOURCE_NOTE[source]}
    />
);

const feesTip = ({ breakdown, total, qty, missing }) => (
    <BreakdownTip
        title="Platform fees"
        rows={FEE_LABELS.map(([k, label]) => [label, money(breakdown?.[k])])}
        total={["Total fees", money(total)]}
        note={missing
            ? "No fee data. Go to your Catalog and run Backfill Fees."
            : qty ? `${money(total / qty)} per unit × ${qty}` : null}
    />
);

const netTip = ({ revenue, cogs, fees, net, missing }) => (
    <BreakdownTip
        title="Net profit"
        rows={[["Revenue", money(revenue)], ["− COGS", money(cogs)], ["− Platform fees", money(fees)]]}
        total={["Net profit", money(net)]}
        note={`${pct(net, revenue)}% net margin${missing ? " · Overstated: some cost data is missing" : ""}`}
    />
);

const SHIP_TIP = "Shipping paid by the buyer. It's already included in Revenue and isn't an expense. Your actual shipping cost is inside Fees (Fulfillment).";

// Wrap any KPI card / total in this to make it clickable.
export const ClickableTotal = ({ onClick, children, title = "Click to see what makes up this number" }) => (
    <div
        role="button"
        tabIndex={0}
        title={title}
        onClick={onClick}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
        className="cursor-pointer rounded-2xl transition hover:ring-2 hover:ring-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-400"
    >
        {children}
    </div>
);

const groupBySku = (rows) => {
    const map = new Map();
    rows.forEach((r) => {
        const key = r.sku || r.catalog_item_id || r.title || "unknown";
        let g = map.get(key);
        if (!g) {
            g = {
                sku: r.sku, catalog_item_id: r.catalog_item_id, title: r.title, supplier: r.supplier,
                image_url: r.image_url, needs_cost: false, needs_fees: false,
                units_sold: 0, _orders: new Set(), revenue: 0, cogs: 0, cost_price_cogs: 0, extra_cogs: 0,
                platform_fees: 0, fee_breakdown: Object.fromEntries(FEE_LABELS.map(([k]) => [k, 0])),
                shipping_cost: 0, net_profit: 0, missing_units: 0,
            };
            map.set(key, g);
        }
        if (!g.image_url && r.image_url) g.image_url = r.image_url;
        if (needsUnitCost(r)) g.needs_cost = true;
        if (needsFees(r)) g.needs_fees = true;
        const qty = Number(r.qty || 0);
        g.units_sold += qty;
        g._orders.add(r.purchase_order_id);
        g.revenue += Number(r.revenue || 0);
        g.cogs += Number(r.cogs || 0);
        g.cost_price_cogs += Number(r.cost_price_cogs || 0);
        g.extra_cogs += Number(r.extra_cogs || 0);
        g.platform_fees += Number(r.platform_fees || 0);
        FEE_LABELS.forEach(([k]) => { g.fee_breakdown[k] += Number(r.fee_breakdown?.[k] || 0); });
        g.shipping_cost += Number(r.shipping_cost || 0);
        g.net_profit += Number(r.net_profit || 0);
        if (r.missing_cost) g.missing_units += qty;
    });
    return [...map.values()].map(({ _orders, ...g }) => ({
        ...g,
        order_count: _orders.size,
        net_margin_pct: g.revenue ? Number(((g.net_profit / g.revenue) * 100).toFixed(1)) : 0,
    }));
};

const SummaryStat = ({ label, value, tone = "text-gray-800", tip }) => (
    <div className="bg-gray-50 rounded-xl px-3 py-2">
        <p className="text-[10px] font-bold text-gray-400 uppercase">{label}</p>
        <p className={`text-sm font-bold ${tone}`}>
            <Tip content={tip} className={tip ? NUM : ""}>{value}</Tip>
        </p>
    </div>
);

const MissingChips = ({ row }) => {
    if (!row.missing_cost) return <span className="text-[10px] font-semibold text-green-600">OK</span>;
    const fields = row.missing_fields?.length ? row.missing_fields : ["unit_cost"];
    return (
        <div className="flex flex-wrap gap-1">
            {fields.map((f) => (
                <Tip key={f} content={MISSING_HELP[f]}>
                    <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                        {MISSING_LABELS[f] || f}
                    </span>
                </Tip>
            ))}
        </div>
    );
};

const PoCell = ({ po }) => {
    const [copied, setCopied] = useState(false);
    if (!po) return <span className="text-gray-300">—</span>;
    const short = po.length > 8 ? `${po.slice(0, 3)}..${po.slice(-3)}` : po;
    const copy = async (e) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(po);
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
        } catch { /* clipboard blocked — ignore */ }
    };
    return (
        <span className="inline-flex items-center gap-1 whitespace-nowrap">
            <Tip content={`PO ${po}`}><span className="font-mono text-[11px]">{short}</span></Tip>
            <button type="button" onClick={copy} className="text-gray-400 hover:text-teal-600" title="Copy PO number">
                {copied ? <Check size={12} className="text-green-600" /> : <Copy size={12} />}
            </button>
        </span>
    );
};

const SupplierCell = ({ supplier }) => (
    !supplier || supplier === "Unassigned"
        ? <Tip content="No supplier set for this item. Add one in your catalog."><span className="text-gray-300">—</span></Tip>
        : <span>{supplier}</span>
);

const PREVIEW_SIZE = 224;

const ItemThumb = ({ url }) => {
    const [broken, setBroken] = useState(false);
    const [preview, setPreview] = useState(null);

    if (!url || broken) {
        return (
            <div className="w-9 h-9 shrink-0 rounded-lg bg-gray-100 flex items-center justify-center">
                <Package size={14} className="text-gray-300" />
            </div>
        );
    }

    // Fixed positioning (viewport-based) so the table's overflow container
    // can't clip the enlarged image. Opens to the right, flips left near the edge.
    const showPreview = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const fitsRight = rect.right + 12 + PREVIEW_SIZE < window.innerWidth;
        const left = fitsRight ? rect.right + 12 : Math.max(8, rect.left - 12 - PREVIEW_SIZE);
        const top = Math.min(
            Math.max(8, rect.top + rect.height / 2 - PREVIEW_SIZE / 2),
            window.innerHeight - PREVIEW_SIZE - 8
        );
        setPreview({ top, left });
    };

    return (
        <>
            <img src={url} alt="" loading="lazy" onError={() => setBroken(true)}
                onMouseEnter={showPreview} onMouseLeave={() => setPreview(null)}
                className="w-9 h-9 shrink-0 rounded-lg object-cover bg-gray-50 border border-gray-100" />
            {preview && (
                <span className="fixed z-[10001] block pointer-events-none bg-white rounded-xl shadow-2xl border border-gray-200 p-1.5"
                    style={{ top: preview.top, left: preview.left, width: PREVIEW_SIZE, height: PREVIEW_SIZE }}>
                    <img src={url} alt="" className="w-full h-full object-contain rounded-lg" />
                </span>
            )}
        </>
    );
};

const SkuCell = ({ row, onOpenSku }) => {
    const clickable = !!(onOpenSku && row.catalog_item_id);
    const open = (e) => {
        e.stopPropagation();
        if (clickable) onOpenSku(row.catalog_item_id);
    };
    return (
        <div className="flex items-center gap-2 max-w-[240px]">
            <button type="button" onClick={open} disabled={!clickable}
                className={clickable ? "cursor-pointer hover:opacity-80" : "cursor-default"}
                title={clickable ? "Open SKU report" : undefined}>
                <ItemThumb url={row.image_url} />
            </button>
            <div className="min-w-0">
                {clickable ? (
                    <button type="button" onClick={open} className="font-semibold text-teal-700 hover:underline">
                        {row.sku || "—"}
                    </button>
                ) : (
                    <span className="font-semibold">{row.sku || "—"}</span>
                )}
                <p className="text-[10px] leading-tight text-gray-400 truncate max-w-[180px]" title={row.title}>{row.title}</p>
            </div>
        </div>
    );
};

// Inline "add buy cost" — saves the item's Cost Price (same as setting it in
// the catalog), so it applies to every order of this SKU not costed from a
// replen lot.
const CostInput = ({ catalogItemId, costDefId, onSaved }) => {
    const [value, setValue] = useState("");
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);

    if (!costDefId) {
        return (
            <Tip content='Create a cost type named "Cost Price" first, then you can add buy costs here.'>
                <span className="text-[10px] font-semibold text-amber-600">No "Cost Price" type</span>
            </Tip>
        );
    }

    const save = async (e) => {
        if (e) e.stopPropagation();
        const n = parseFloat(value);
        if (!Number.isFinite(n) || n <= 0) {
            setErr("Enter a cost above $0");
            return;
        }
        setSaving(true);
        setErr(null);
        try {
            await setItemCosts([catalogItemId], costDefId, n);
            setValue("");
            await onSaved();
        } catch (ex) {
            setErr(ex.message || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="flex flex-col gap-0.5" onClick={(e) => e.stopPropagation()}>
            <Tip content="Sets this item's Cost Price. Applies to every order of this SKU that isn't costed from a replen lot.">
                <span className="flex items-center gap-1">
                    <span className="text-gray-400">$</span>
                    <input type="number" step="0.01" min="0" value={value} placeholder="Buy cost"
                        onChange={(e) => { setValue(e.target.value); setErr(null); }}
                        onKeyDown={(e) => { if (e.key === "Enter") save(); }}
                        className="w-20 px-1.5 py-1 border border-amber-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-teal-300" />
                    <button type="button" onClick={save} disabled={saving || !value}
                        className="px-2 py-1 text-[10px] font-bold rounded bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50">
                        {saving ? "..." : "Save"}
                    </button>
                </span>
            </Tip>
            {err && <span className="text-[10px] text-red-600">{err}</span>}
        </div>
    );
};

const LineItemsDrawer = ({
    open, onClose, title, subtitle, params = {},
    defaultView = "lines", defaultMissingOnly = false,
    reconcile, // optional { label, field, expected } — checks lines add up to the clicked total
    onCostSaved, // optional — called after a buy cost is saved, so the report behind can refresh
}) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [reloading, setReloading] = useState(false);
    const [error, setError] = useState(null);
    const [view, setView] = useState(defaultView);
    const [missingOnly, setMissingOnly] = useState(defaultMissingOnly);
    const [search, setSearch] = useState("");
    const [costsEdited, setCostsEdited] = useState(false);
    const [skuPopupId, setSkuPopupId] = useState(null);
    const paramsKey = JSON.stringify(params || {});

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setView(defaultView);
        setMissingOnly(defaultMissingOnly);
        setSearch("");
        setCostsEdited(false);
        setSkuPopupId(null);
        setLoading(true);
        setError(null);
        setData(null);
        getReportLineItems(params)
            .then((d) => { if (!cancelled) setData(d); })
            .catch((e) => { if (!cancelled) setError(e.message || "Failed to load order lines"); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, paramsKey, defaultView, defaultMissingOnly]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key !== "Escape") return;
            if (skuPopupId) setSkuPopupId(null);
            else onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose, skuPopupId]);

    const allRows = useMemo(() => data?.rows || [], [data]);
    const missingRows = useMemo(() => allRows.filter((r) => r.missing_cost), [allRows]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return allRows.filter((r) => {
            if (missingOnly && !r.missing_cost) return false;
            if (!q) return true;
            return [r.sku, r.title, r.purchase_order_id, r.supplier]
                .some((v) => (v ?? "").toString().toLowerCase().includes(q));
        });
    }, [allRows, missingOnly, search]);

    const skuRows = useMemo(() => groupBySku(filtered), [filtered]);

    const lines = useSortAndPaginate(filtered, { key: "order_date", dir: "desc" }, "reports:drawer:lines");
    const skus = useSortAndPaginate(skuRows, { key: "units_sold", dir: "desc" }, "reports:drawer:skus");

    useEffect(() => {
        lines.setPage(1);
        skus.setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [missingOnly, search, paramsKey]);

    if (!open) return null;

    const costDefId = data?.cost_price_definition_id || null;

    // Re-fetch lines in place (no flicker, filters kept) after a cost save,
    // then let the report behind refresh its own totals.
    const handleCostSaved = async () => {
        setCostsEdited(true);
        setReloading(true);
        try {
            const d = await getReportLineItems(params);
            setData(d);
        } catch (e) {
            setError(e.message || "Failed to reload order lines");
        } finally {
            setReloading(false);
        }
        if (onCostSaved) onCostSaved();
    };

    const reconcileActual = reconcile ? sum(allRows, reconcile.field) : null;
    const reconcileOk = reconcile && Math.abs(reconcileActual - Number(reconcile.expected || 0)) < 0.015;
    // Revenue/units don't change when a cost is saved; COGS/fees/profit do,
    // so the number captured when the drawer opened is no longer comparable.
    const reconcileStale = costsEdited && reconcile && !["revenue", "qty"].includes(reconcile.field);

    // Summary totals (reflect current filters) + their hover breakdowns
    const s = {
        qty: sum(filtered, "qty"),
        revenue: sum(filtered, "revenue"),
        ship: sum(filtered, "shipping_cost"),
        cogs: sum(filtered, "cogs"),
        costPrice: sum(filtered, "cost_price_cogs"),
        extra: sum(filtered, "extra_cogs"),
        fees: sum(filtered, "platform_fees"),
        feeBreakdown: sumFeeBreakdown(filtered),
        net: sum(filtered, "net_profit"),
        anyMissing: filtered.some((r) => r.missing_cost),
        anyMissingCost: filtered.some(needsUnitCost),
        anyMissingFees: filtered.some(needsFees),
    };

    const fileSlug = (title || "breakdown").toLowerCase().replace(/[^a-z0-9]+/g, "_");
    const today = new Date().toISOString().slice(0, 10);

    const exportCsv = () => {
        if (view === "skus") {
            downloadCsv(
                `${fileSlug}_by_listing_${today}.csv`,
                ["SKU", "Title", "Supplier", "Units", "Orders", "Revenue", "COGS", "Platform Fees", "Ship Charged", "Net Profit", "Net Margin %", "Units Missing Cost"],
                skus.sorted.map((g) => [g.sku, g.title, g.supplier, g.units_sold, g.order_count, g.revenue.toFixed(2), g.cogs.toFixed(2),
                g.platform_fees.toFixed(2), g.shipping_cost.toFixed(2), g.net_profit.toFixed(2), g.net_margin_pct, g.missing_units]),
                [["Revenue", s.revenue.toFixed(2)], ["Net Profit", s.net.toFixed(2)]]
            );
        } else {
            downloadCsv(
                `${fileSlug}_order_lines_${today}.csv`,
                ["Date", "PO #", "SKU", "Title", "Supplier", "Qty", "Revenue", "Unit Cost", "COGS", "Platform Fees", "Ship Charged", "Net Profit", "Missing Cost", "Missing Fields"],
                lines.sorted.map((r) => [r.order_date, r.purchase_order_id, r.sku, r.title, r.supplier, r.qty,
                Number(r.revenue || 0).toFixed(2), r.unit_cost == null ? "" : Number(r.unit_cost).toFixed(2),
                Number(r.cogs || 0).toFixed(2), Number(r.platform_fees || 0).toFixed(2), Number(r.shipping_cost || 0).toFixed(2),
                Number(r.net_profit || 0).toFixed(2), r.missing_cost ? "YES" : "", (r.missing_fields || []).join("; ")]),
                [["Revenue", s.revenue.toFixed(2)], ["Net Profit", s.net.toFixed(2)]]
            );
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex justify-end">
            <div className="absolute inset-0 bg-black/30" onClick={onClose} />

            {/* SKU report popup — sits above the drawer; drawer stays open underneath */}
            {skuPopupId && (
                <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setSkuPopupId(null)} />
                    <div className="relative bg-gray-50 rounded-2xl shadow-2xl w-full max-w-6xl max-h-[92vh] overflow-y-auto">
                        <button type="button" onClick={() => setSkuPopupId(null)}
                            className="absolute top-3 right-3 z-10 p-2 rounded-lg bg-white shadow text-gray-500 hover:text-gray-800">
                            <X size={18} />
                        </button>
                        <SkuDetailPage catalogItemId={skuPopupId} onBack={() => setSkuPopupId(null)} />
                    </div>
                </div>
            )}

            <div className="relative w-full max-w-6xl h-full bg-white shadow-2xl flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 flex items-start justify-between gap-4">
                    <div>
                        <h2 className="text-lg font-bold text-gray-800">{title}</h2>
                        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
                        {reloading && <p className="text-[11px] text-teal-600 font-semibold">Updating lines…</p>}
                    </div>
                    <div className="flex items-center gap-2">
                        {data && <ExportCsvButton label="Export" onExport={exportCsv} />}
                        <button type="button" onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100">
                            <X size={18} />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {loading && <div className="py-16 text-center text-gray-400 text-sm">Loading order lines...</div>}
                    {error && <div className="py-4 text-center text-red-500 text-sm">{error}</div>}

                    {data && !loading && (
                        <>
                            {/* Reconcile check */}
                            {reconcile && (reconcileStale ? (
                                <div className="mb-3 text-xs rounded-xl px-3 py-2 bg-gray-50 text-gray-600">
                                    Costs were updated, so {reconcile.label} changed. The report is refreshing in the background. Reopen this breakdown to re-check the total.
                                </div>
                            ) : (
                                <div className={`mb-3 text-xs rounded-xl px-3 py-2 flex items-center gap-2 ${reconcileOk ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                                    {reconcileOk ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
                                    {reconcileOk
                                        ? `These ${allRows.length} lines add up to the report's ${reconcile.label} (${money(reconcile.expected)}).`
                                        : `Mismatch: lines add up to ${money(reconcileActual)} ${reconcile.label}, but the report shows ${money(reconcile.expected)}. Difference ${money(reconcileActual - reconcile.expected)}.`}
                                </div>
                            ))}

                            {/* Missing cost banner */}
                            {missingRows.length > 0 && (
                                <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                                    <div className="flex items-start gap-2 text-xs text-amber-800">
                                        <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                                        <span>
                                            <b>{missingRows.length} line{missingRows.length === 1 ? "" : "s"}</b> ({sum(missingRows, "qty")} units,{" "}
                                            {money(sum(missingRows, "revenue"))} revenue) are missing cost data, so profit here is overstated.
                                            {" "}Add a buy cost right in the table below. Hover a yellow tag to see how to fix it.
                                        </span>
                                    </div>
                                    <button type="button" onClick={() => setMissingOnly((v) => !v)}
                                        className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-amber-600 text-white hover:bg-amber-700">
                                        {missingOnly ? "Show all lines" : "Show only these"}
                                    </button>
                                </div>
                            )}

                            {/* Summary (reflects current filters) — hover any number for its breakdown */}
                            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-4">
                                <SummaryStat label="Lines" value={filtered.length}
                                    tip={`${filtered.length} order lines across ${new Set(filtered.map((r) => r.purchase_order_id)).size} orders`} />
                                <SummaryStat label="Units" value={s.qty}
                                    tip={`${s.qty} units across ${new Set(filtered.map((r) => r.sku || r.catalog_item_id)).size} listings`} />
                                <SummaryStat label="Revenue" value={money(s.revenue)} tone="text-teal-700"
                                    tip={revenueTip(s.revenue, s.qty, s.ship)} />
                                <SummaryStat label="COGS" value={money(s.cogs)} tone="text-orange-600"
                                    tip={cogsTip({ costPrice: s.costPrice, extra: s.extra, cogs: s.cogs, missing: s.anyMissingCost })} />
                                <SummaryStat label="Platform Fees" value={money(s.fees)} tone="text-red-600"
                                    tip={feesTip({ breakdown: s.feeBreakdown, total: s.fees, qty: s.qty, missing: s.anyMissingFees })} />
                                <SummaryStat label="Net Profit" value={money(s.net)}
                                    tone={s.net >= 0 ? "text-green-600" : "text-red-600"}
                                    tip={netTip({ revenue: s.revenue, cogs: s.cogs, fees: s.fees, net: s.net, missing: s.anyMissing })} />
                            </div>

                            {/* Controls */}
                            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                                <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden text-xs font-semibold">
                                    <button type="button" onClick={() => setView("lines")}
                                        className={`flex items-center gap-1 px-3 py-1.5 ${view === "lines" ? "bg-teal-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}>
                                        <List size={13} /> By order line
                                    </button>
                                    <button type="button" onClick={() => setView("skus")}
                                        className={`flex items-center gap-1 px-3 py-1.5 ${view === "skus" ? "bg-teal-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}>
                                        <Layers size={13} /> By listing
                                    </button>
                                </div>
                                <div className="flex items-center gap-3">
                                    <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                                        <input type="checkbox" checked={missingOnly} onChange={(e) => setMissingOnly(e.target.checked)} />
                                        Missing cost only
                                    </label>
                                    <div className="relative">
                                        <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="SKU, title, PO, supplier"
                                            className="pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded-lg w-56 focus:outline-none focus:ring-2 focus:ring-teal-300" />
                                    </div>
                                </div>
                            </div>

                            {/* Tables */}
                            <div className="overflow-x-auto">
                                {view === "lines" ? (
                                    <table className="w-full text-xs">
                                        <thead className="sticky top-0 z-10 bg-white">
                                            <tr className="text-left text-gray-400 border-b border-gray-100">
                                                <SortableTH label="Date" sortKey="order_date" sortConfig={lines.sortConfig} onSort={lines.requestSort} />
                                                <SortableTH label="PO #" sortKey="purchase_order_id" sortConfig={lines.sortConfig} onSort={lines.requestSort} />
                                                <SortableTH label="Item" sortKey="sku" sortConfig={lines.sortConfig} onSort={lines.requestSort} />
                                                <SortableTH label="Supplier" sortKey="supplier" sortConfig={lines.sortConfig} onSort={lines.requestSort} />
                                                <SortableTH label="Qty" sortKey="qty" sortConfig={lines.sortConfig} onSort={lines.requestSort} />
                                                <SortableTH label="Revenue" sortKey="revenue" sortConfig={lines.sortConfig} onSort={lines.requestSort} />
                                                <SortableTH label="Unit Cost" sortKey="unit_cost" sortConfig={lines.sortConfig} onSort={lines.requestSort} />
                                                <SortableTH label="COGS" sortKey="cogs" sortConfig={lines.sortConfig} onSort={lines.requestSort} />
                                                <SortableTH label="Fees" sortKey="platform_fees" sortConfig={lines.sortConfig} onSort={lines.requestSort} />
                                                <SortableTH label="Ship Charged" sortKey="shipping_cost" sortConfig={lines.sortConfig} onSort={lines.requestSort} />
                                                <SortableTH label="Net" sortKey="net_profit" sortConfig={lines.sortConfig} onSort={lines.requestSort} />
                                                <SortableTH label="Cost Data" sortKey="missing_cost" sortConfig={lines.sortConfig} onSort={lines.requestSort} />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {lines.pageItems.map((r) => (
                                                <tr key={r.order_line_id ?? `${r.purchase_order_id}-${r.line_number}`}
                                                    className={`border-b border-gray-50 ${r.missing_cost ? "bg-amber-50/60" : ""}`}>
                                                    <td className="py-2 whitespace-nowrap">{shortDate(r.order_date)}</td>
                                                    <td className="py-2"><PoCell po={r.purchase_order_id} /></td>
                                                    <td className="py-2"><SkuCell row={r} onOpenSku={setSkuPopupId} /></td>
                                                    <td className="py-2"><SupplierCell supplier={r.supplier} /></td>
                                                    <td className="py-2">{r.qty}</td>
                                                    <td className="py-2">
                                                        <Tip content={revenueTip(r.revenue, r.qty, r.shipping_cost)} className={NUM}>{money(r.revenue)}</Tip>
                                                    </td>
                                                    <td className="py-2">
                                                        {r.unit_cost != null ? (
                                                            <Tip className={NUM} content={
                                                                <BreakdownTip title="Unit cost"
                                                                    rows={[["COGS", money(r.cogs)], ["÷ Qty", r.qty]]}
                                                                    total={["Per unit", money(r.unit_cost)]} />
                                                            }>{money(r.unit_cost)}</Tip>
                                                        ) : (needsUnitCost(r) && r.catalog_item_id) ? (
                                                            <CostInput catalogItemId={r.catalog_item_id} costDefId={costDefId} onSaved={handleCostSaved} />
                                                        ) : (
                                                            <Tip content={MISSING_HELP.unlinked}><span className="text-amber-600 font-semibold">—</span></Tip>
                                                        )}
                                                    </td>
                                                    <td className="py-2">
                                                        <Tip className={NUM} content={cogsTip({
                                                            costPrice: r.cost_price_cogs, extra: r.extra_cogs, cogs: r.cogs,
                                                            source: r.cogs_source, missing: needsUnitCost(r),
                                                        })}>{money(r.cogs)}</Tip>
                                                    </td>
                                                    <td className="py-2 text-red-600">
                                                        <Tip className={NUM} content={feesTip({
                                                            breakdown: r.fee_breakdown, total: r.platform_fees, qty: r.qty, missing: needsFees(r),
                                                        })}>{money(r.platform_fees)}</Tip>
                                                    </td>
                                                    <td className="py-2">
                                                        <Tip content={SHIP_TIP} className={NUM}>{money(r.shipping_cost)}</Tip>
                                                    </td>
                                                    <td className={`py-2 font-bold ${Number(r.net_profit) >= 0 ? "text-green-600" : "text-red-600"}`}>
                                                        <Tip className={NUM} content={netTip({
                                                            revenue: r.revenue, cogs: r.cogs, fees: r.platform_fees, net: r.net_profit, missing: r.missing_cost,
                                                        })}>{money(r.net_profit)}</Tip>
                                                    </td>
                                                    <td className="py-2"><MissingChips row={r} /></td>
                                                </tr>
                                            ))}
                                            {lines.pageItems.length === 0 && (
                                                <tr><td colSpan={12} className="py-8 text-center text-gray-400">No order lines match</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                ) : (
                                    <table className="w-full text-xs">
                                        <thead className="sticky top-0 z-10 bg-white">
                                            <tr className="text-left text-gray-400 border-b border-gray-100">
                                                <SortableTH label="Listing" sortKey="sku" sortConfig={skus.sortConfig} onSort={skus.requestSort} />
                                                <SortableTH label="Supplier" sortKey="supplier" sortConfig={skus.sortConfig} onSort={skus.requestSort} />
                                                <SortableTH label="Units" sortKey="units_sold" sortConfig={skus.sortConfig} onSort={skus.requestSort} />
                                                <SortableTH label="Orders" sortKey="order_count" sortConfig={skus.sortConfig} onSort={skus.requestSort} />
                                                <SortableTH label="Revenue" sortKey="revenue" sortConfig={skus.sortConfig} onSort={skus.requestSort} />
                                                <SortableTH label="COGS" sortKey="cogs" sortConfig={skus.sortConfig} onSort={skus.requestSort} />
                                                <SortableTH label="Fees" sortKey="platform_fees" sortConfig={skus.sortConfig} onSort={skus.requestSort} />
                                                <SortableTH label="Net" sortKey="net_profit" sortConfig={skus.sortConfig} onSort={skus.requestSort} />
                                                <SortableTH label="Margin" sortKey="net_margin_pct" sortConfig={skus.sortConfig} onSort={skus.requestSort} />
                                                <SortableTH label="Missing Cost" sortKey="missing_units" sortConfig={skus.sortConfig} onSort={skus.requestSort} />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {skus.pageItems.map((g) => (
                                                <tr key={g.sku || g.catalog_item_id || g.title} className={`border-b border-gray-50 ${g.missing_units > 0 ? "bg-amber-50/60" : ""}`}>
                                                    <td className="py-2"><SkuCell row={g} onOpenSku={setSkuPopupId} /></td>
                                                    <td className="py-2"><SupplierCell supplier={g.supplier} /></td>
                                                    <td className="py-2 font-semibold">
                                                        <Tip className={NUM} content={`${g.units_sold} units across ${g.order_count} orders (avg ${(g.units_sold / Math.max(1, g.order_count)).toFixed(1)} per order)`}>
                                                            {g.units_sold}
                                                        </Tip>
                                                    </td>
                                                    <td className="py-2">{g.order_count}</td>
                                                    <td className="py-2">
                                                        <Tip className={NUM} content={revenueTip(g.revenue, g.units_sold, g.shipping_cost)}>{money(g.revenue)}</Tip>
                                                    </td>
                                                    <td className="py-2">
                                                        <Tip className={NUM} content={cogsTip({
                                                            costPrice: g.cost_price_cogs, extra: g.extra_cogs, cogs: g.cogs, missing: g.needs_cost,
                                                        })}>{money(g.cogs)}</Tip>
                                                    </td>
                                                    <td className="py-2 text-red-600">
                                                        <Tip className={NUM} content={feesTip({
                                                            breakdown: g.fee_breakdown, total: g.platform_fees, qty: g.units_sold, missing: g.needs_fees,
                                                        })}>{money(g.platform_fees)}</Tip>
                                                    </td>
                                                    <td className={`py-2 font-bold ${g.net_profit >= 0 ? "text-green-600" : "text-red-600"}`}>
                                                        <Tip className={NUM} content={netTip({
                                                            revenue: g.revenue, cogs: g.cogs, fees: g.platform_fees, net: g.net_profit, missing: g.missing_units > 0,
                                                        })}>{money(g.net_profit)}</Tip>
                                                    </td>
                                                    <td className="py-2">
                                                        <Tip className={NUM} content={`Net profit ÷ revenue = ${money(g.net_profit)} ÷ ${money(g.revenue)}`}>
                                                            {g.net_margin_pct}%
                                                        </Tip>
                                                    </td>
                                                    <td className="py-2">
                                                        <div className="flex flex-col gap-1 items-start">
                                                            {g.missing_units > 0 ? (
                                                                <Tip content={[
                                                                    g.needs_cost ? MISSING_HELP.unit_cost : null,
                                                                    g.needs_fees ? MISSING_HELP.fees : null,
                                                                ].filter(Boolean).join(" ") || MISSING_HELP.unlinked}>
                                                                    <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">{g.missing_units} units</span>
                                                                </Tip>
                                                            ) : (
                                                                <span className="text-[10px] font-semibold text-green-600">OK</span>
                                                            )}
                                                            {g.needs_cost && g.catalog_item_id && (
                                                                <CostInput catalogItemId={g.catalog_item_id} costDefId={costDefId} onSaved={handleCostSaved} />
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                            {skus.pageItems.length === 0 && (
                                                <tr><td colSpan={10} className="py-8 text-center text-gray-400">No listings match</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                            {view === "lines"
                                ? <Pagination page={lines.page} totalPages={lines.totalPages} setPage={lines.setPage} totalItems={lines.sorted.length} pageSize={lines.pageSize} />
                                : <Pagination page={skus.page} totalPages={skus.totalPages} setPage={skus.setPage} totalItems={skus.sorted.length} pageSize={skus.pageSize} />}
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default LineItemsDrawer;